// ============================================================
// Group Buy Flats — Frontend API Service
// Frontend: Hostinger | Backend: Render | DB: Supabase
// ============================================================

const BASE_URL = process.env.REACT_APP_API_URL || "http://groupbuyflats-production.up.railway.app";

async function apiFetch(path, options = {}) {
  const token = localStorage.getItem("gbf_admin_token");
  const res = await fetch(`${BASE_URL}${path}`, {
    headers: {
      "Content-Type": "application/json",
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
    ...options,
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message || "Request failed");
  return data;
}

// ─── AUTH ─────────────────────────────────────────────────────
export const authAPI = {
  login: (email, password) =>
    apiFetch("/auth/login", { method: "POST", body: JSON.stringify({ email, password }) }),
  me: () => apiFetch("/auth/me"),
  logout: () => localStorage.removeItem("gbf_admin_token"),
};

// ─── LEADS ────────────────────────────────────────────────────
export const leadsAPI = {
  create: (lead) =>
    apiFetch("/leads", { method: "POST", body: JSON.stringify(lead) }),
  list: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return apiFetch(`/leads?${qs}`);
  },
  get: (id) => apiFetch(`/leads/${id}`),
  update: (id, data) =>
    apiFetch(`/leads/${id}`, { method: "PUT", body: JSON.stringify(data) }),
  bulkAssign: (leadIds, assignedTo) =>
    apiFetch(`/leads/bulk-assign`, {
      method: "PUT",
      body: JSON.stringify({ lead_ids: leadIds, assigned_to: assignedTo || null }),
    }),
  delete: (id) => apiFetch(`/leads/${id}`, { method: "DELETE" }),
  analytics: () => apiFetch("/leads/analytics/summary"),
  exportCSV: async (params = {}) => {
    const token = localStorage.getItem("gbf_admin_token");
    const qs = new URLSearchParams(params).toString();
    try {
      const res = await fetch(`${BASE_URL}/leads/export/csv?${qs}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `gbf-leads-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      alert("CSV export failed: " + err.message);
    }
  },
};

// ─── ADMINS (sub-admin management) ────────────────────────────
export const adminsAPI = {
  list: () => apiFetch("/admins"),
  create: (data) =>
    apiFetch("/admins", { method: "POST", body: JSON.stringify(data) }),
  update: (id, data) =>
    apiFetch(`/admins/${id}`, { method: "PUT", body: JSON.stringify(data) }),
  delete: (id) => apiFetch(`/admins/${id}`, { method: "DELETE" }),
};

// ─── PROPERTIES ───────────────────────────────────────────────
export const propertiesAPI = {
  list: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return apiFetch(`/properties?${qs}`);
  },
  get: (idOrSlug) => apiFetch(`/properties/${idOrSlug}`),
  filterMeta: () => apiFetch("/properties/meta/filters"),
  create: (data) =>
    apiFetch("/properties", { method: "POST", body: JSON.stringify(data) }),
  update: (id, data) =>
    apiFetch(`/properties/${id}`, { method: "PUT", body: JSON.stringify(data) }),
  delete: (id) => apiFetch(`/properties/${id}`, { method: "DELETE" }),
  addMedia: (id, data) =>
    apiFetch(`/properties/${id}/media`, { method: "POST", body: JSON.stringify(data) }),
  deleteMedia: (propId, mediaId) =>
    apiFetch(`/properties/${propId}/media/${mediaId}`, { method: "DELETE" }),
};

// ─── PROJECTS (from XLSX import → Supabase) ───────────────────
export const projectsAPI = {
  list: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return apiFetch(`/projects${qs ? `?${qs}` : ""}`);
  },
  get: (idOrSlug) => apiFetch(`/projects/${idOrSlug}`),
  filterMeta: () => apiFetch("/projects/meta/filters"),
  create: (data) =>
    apiFetch("/projects", { method: "POST", body: JSON.stringify(data) }),
  update: (id, data) =>
    apiFetch(`/projects/${id}`, { method: "PUT", body: JSON.stringify(data) }),
  delete: (id) => apiFetch(`/projects/${id}`, { method: "DELETE" }),
};

// Map a raw Supabase `projects` row → the shape the UI components expect.
// Falls back to a rotating stock image when no hosted elevation URL exists
// (the imported paths are local Windows paths, not web URLs).
const PROJECT_IMAGE_POOL = [
  "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=1200&q=80",
  "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=1200&q=80",
  "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=1200&q=80",
  "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1200&q=80",
  "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1200&q=80",
  "https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=1200&q=80",
];

function isWebUrl(v) {
  return typeof v === "string" && /^https?:\/\//i.test(v);
}

// ─── DISPLAY NAME NORMALISER ─────────────────────────────────
// DB values come in with irregular casing, e.g. "Oorjita projects",
// "Dnb Constructions". This title-cases each word while preserving
// known acronyms (DNB, DLF, LVS, RERA, etc.) in full upper-case.
const KNOWN_ACRONYMS = new Set([
  "DNB", "DLF", "LVS", "RERA", "HMDA", "SBR", "MJR", "PBEL", "NCC",
  "SMR", "GHR", "ASBL", "BSR", "RKS", "VRR", "EIPL", "SAS",
]);

export function prettifyName(value) {
  if (!value || typeof value !== "string") return value || "";
  return value
    .trim()
    .split(/\s+/)
    .map((word) => {
      const upper = word.toUpperCase();
      if (KNOWN_ACRONYMS.has(upper)) return upper;
      // Keep existing internal capitals (e.g. "GroupBuy", "McCoy"),
      // otherwise capitalise the first letter of the word.
      if (/[A-Z]/.test(word.slice(1))) {
        return word.charAt(0).toUpperCase() + word.slice(1);
      }
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");
}

export function mapProjectToProperty(p) {
  const img = isWebUrl(p.project_elevation_path)
    ? p.project_elevation_path
    : PROJECT_IMAGE_POOL[(p.sno || 0) % PROJECT_IMAGE_POOL.length];

  return {
    id: p.slug || String(p.sno),
    slug: p.slug,
    sno: p.sno,
    title: prettifyName(p.project_name),
    projectRaw: p.project_name || "",
    builder: p.builder_name ? prettifyName(p.builder_name) : "—",
    builderRaw: p.builder_name || "",
    type: p.property_type || "Apartment",
    zone: p.zone,
    area: p.area,
    city: "Hyderabad",
    price: "Group-negotiated price",
    bhk: p.size || p.bhk || "On Request",
    acres: p.acres,
    units: p.units,
    floors: p.floors,
    status: "Available",
    description: p.project_narration || p.builder_intro || "",
    builderIntro: p.builder_intro || "",
    highlights: [
      p.units && `${p.units}`,
      p.acres && `${p.acres}`,
      p.floors && `${p.floors}`,
      p.zone && `${p.zone} Zone`,
    ].filter(Boolean),
    images: [img],
    video: null,
    brochure: isWebUrl(p.website_link) ? p.website_link : null,
    websiteLink: p.website_link,
    logo: isWebUrl(p.builder_logo_path) ? p.builder_logo_path : null,
    lat: p.latitude ? Number(p.latitude) : null,
    lng: p.longitude ? Number(p.longitude) : null,
    googleMapLink: p.google_map_link || null,
    address: p.physical_address,
    featured: !!p.is_featured,
  };
}

// ─── SUPABASE STORAGE (public bucket: project-images) ─────────
// The frontend has no Supabase client; we build public URLs directly.
// Folder per project is named after `project_name` (spaces & caps kept),
// e.g.  project-images/Bhanodaya Crystals/1.jpeg
export const SUPABASE_URL =
  process.env.REACT_APP_SUPABASE_URL || "https://rhjsssnhqeuvknqhfvax.supabase.co";
export const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || "";
export const PROJECT_IMAGES_BUCKET = "project-images";

// Build the public URL for a single file inside a project's folder.
export function projectImageUrl(projectName, fileName) {
  const folder = encodeURIComponent((projectName || "").trim());
  const file = encodeURIComponent(fileName);
  return `${SUPABASE_URL}/storage/v1/object/public/${PROJECT_IMAGES_BUCKET}/${folder}/${file}`;
}

// Returns a candidate list of image URLs to probe (1..max, common extensions).
// The BuilderPage loads these with onError fallbacks and keeps the ones
// that actually exist — no backend / folder-listing needed.
export function projectImageCandidates(projectName, max = 15) {
  const exts = ["jpeg", "jpg", "png", "webp", "JPEG", "JPG", "PNG"];
  const out = [];
  for (let i = 1; i <= max; i++) {
    out.push({
      index: i,
      urls: exts.map((e) => projectImageUrl(projectName, `${i}.${e}`)),
    });
  }
  return out;
}

// ─── LIST ALL IMAGES IN A STORAGE FOLDER ──────────────────────
// The bucket is PUBLIC, but the Storage "list" endpoint still needs
// the anon apikey header. Given a folder name (e.g. a builder name
// like "Cassagrand"), this returns the public URLs of EVERY image
// file inside it — regardless of filename. This is what powers the
// builder-page hero carousel.
//
// Requires REACT_APP_SUPABASE_ANON_KEY. If it's missing, returns []
// and the caller falls back to other image sources.
const IMAGE_EXT_RE = /\.(jpe?g|png|webp|avif|gif)$/i;

export async function listFolderImages(folderName) {
  const folder = (folderName || "").trim().replace(/\/+$/, "");
  if (!folder || !SUPABASE_ANON_KEY) return [];

  try {
    const res = await fetch(
      `${SUPABASE_URL}/storage/v1/object/list/${PROJECT_IMAGES_BUCKET}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          // IMPORTANT: Supabase Storage `list` descends into a folder only
          // when the prefix ends with a slash. Without it, the API returns
          // the folder placeholder (metadata: null) instead of the files,
          // and nothing matches the image-extension filter.
          prefix: `${folder}/`,
          limit: 100,
          offset: 0,
          sortBy: { column: "name", order: "asc" },
        }),
      }
    );
    if (!res.ok) return [];
    const items = await res.json();
    if (!Array.isArray(items)) return [];

    return items
      .filter(
        (it) =>
          it &&
          it.name &&
          it.id !== null &&            // skip sub-folder placeholders
          IMAGE_EXT_RE.test(it.name)
      )
      .map((it) => projectImageUrl(folder, it.name));
  } catch (_) {
    return [];
  }
}


// Recursive version: given a folder prefix (e.g. a builder folder), lists
// every image found either directly inside it OR one level deeper (inside
// its subfolders, e.g. per-project folders). Used for builder-wide
// galleries where the bucket is nested Builder/Project/*.jpg.
export async function listFolderImagesDeep(folderName) {
  const folder = (folderName || "").trim().replace(/\/+$/, "");
  if (!folder || !SUPABASE_ANON_KEY) return [];

  const direct = await listFolderImages(folder);
  const subfolders = await listSubfolders(folder);
  if (!subfolders.length) return direct;

  const nested = await Promise.all(
    subfolders.map((sf) => listFolderImages(`${folder}/${sf}`))
  );
  return [...direct, ...nested.flat()];
}

// ─── LIST SUBFOLDERS AT A GIVEN PREFIX (cached per prefix) ─────
// Bucket layout is nested: <Builder>/<Project>/<images>. Given a prefix
// (e.g. "" for top-level builders, or "Casagrand/" for that builder's
// projects), returns the names of every "folder" directly under it.
// Supabase represents folders as entries whose `id` is null. Cached per
// prefix for the session so repeat lookups don't re-hit Supabase.
const _subfolderCache = new Map(); // prefix → Promise<string[]>

async function listSubfolders(prefix = "") {
  const key = (prefix || "").trim().replace(/\/+$/, "");
  const cacheKey = key ? `${key}/` : "";
  if (_subfolderCache.has(cacheKey)) return _subfolderCache.get(cacheKey);

  const promise = (async () => {
    if (!SUPABASE_ANON_KEY) return [];
    try {
      const res = await fetch(
        `${SUPABASE_URL}/storage/v1/object/list/${PROJECT_IMAGES_BUCKET}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            apikey: SUPABASE_ANON_KEY,
            Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            prefix: cacheKey,
            limit: 1000,
            offset: 0,
            sortBy: { column: "name", order: "asc" },
          }),
        }
      );
      if (!res.ok) return [];
      const items = await res.json();
      if (!Array.isArray(items)) return [];
      // Folders are the placeholder rows (id === null). Some buckets also
      // return bare folder names with no id field at all — accept both.
      return items
        .filter((it) => it && it.name && (it.id === null || it.id === undefined))
        .map((it) => it.name.replace(/\/+$/, ""));
    } catch (_) {
      return [];
    }
  })();

  _subfolderCache.set(cacheKey, promise);
  return promise;
}

// Back-compat alias: top-level folders = builder folders.
async function listBucketFolders() {
  return listSubfolders("");
}

// Normalise a name for fuzzy comparison: lowercase, strip punctuation, and
// drop common company suffixes/descriptors that differ between the display
// name and the folder name (e.g. "Pvt", "Ltd", "Group", "Developers").
const _STOP_WORDS = new Set([
  "pvt", "private", "ltd", "limited", "llp", "inc", "co", "company",
  "group", "groups", "developers", "developer", "constructions",
  "construction", "builders", "builder", "projects", "project",
  "infra", "infrastructure", "homes", "estates", "estate", "realtors",
  "realty", "ventures", "spaces", "engineers", "syndicate", "consultants",
  "and", "the", "&",
]);

function normaliseName(name) {
  return (name || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s]+/g, " ") // punctuation → space
    .split(/\s+/)
    .filter(Boolean)
    .join(" ")
    .trim();
}

function significantTokens(name) {
  return normaliseName(name)
    .split(/\s+/)
    .filter((t) => t && !_STOP_WORDS.has(t));
}

// Given a target display name and a list of REAL folder names, find the
// best fuzzy match. Returns the actual folder name (preserving its real
// casing/spelling) or null if nothing matches. Match priority:
//   1. Exact normalised equality.
//   2. One normalised name is a prefix of / contained in the other.
//   3. Significant-token overlap (all of the shorter side's tokens present).
function findBestFolderMatch(targetName, folders) {
  if (!folders.length) return null;

  const target = normaliseName(targetName);
  const targetTokens = significantTokens(targetName);
  if (!target) return null;

  // 1. exact normalised match
  for (const f of folders) {
    if (normaliseName(f) === target) return f;
  }

  // 2. containment (handles "Bliss Ventures" vs "Bliss Ventures Pvt", etc.)
  let best = null;
  for (const f of folders) {
    const nf = normaliseName(f);
    if (!nf) continue;
    if (nf.startsWith(target) || target.startsWith(nf) || nf.includes(target) || target.includes(nf)) {
      // Prefer the longest folder match (most specific) to avoid e.g. "Sri"
      // grabbing the wrong "Sri ..." folder.
      if (!best || normaliseName(best).length < nf.length) best = f;
    }
  }
  if (best) return best;

  // 3. significant-token overlap. Require that ALL significant tokens of the
  // shorter side appear in the other — this keeps "Sri Developers" and
  // "Sri Karthikeya Buggas" distinct (they share only the stop-ish "sri").
  let tokenBest = null;
  let tokenBestScore = 0;
  for (const f of folders) {
    const fTokens = significantTokens(f);
    if (!fTokens.length || !targetTokens.length) continue;
    const shorter = targetTokens.length <= fTokens.length ? targetTokens : fTokens;
    const longerSet = new Set(targetTokens.length <= fTokens.length ? fTokens : targetTokens);
    const allPresent = shorter.every((t) => longerSet.has(t));
    if (allPresent) {
      const score = shorter.length; // more shared significant tokens = better
      if (score > tokenBestScore) {
        tokenBestScore = score;
        tokenBest = f;
      }
    }
  }
  return tokenBest;
}

// Given a builder display name, find the REAL top-level bucket folder that
// best matches it.
async function resolveBuilderFolder(builderName) {
  const folders = await listBucketFolders();
  return findBestFolderMatch(builderName, folders);
}

// Given an ALREADY-RESOLVED builder folder name and a project display name,
// find the REAL subfolder (inside that builder's folder) that best matches
// the project. Returns null if the builder folder has no matching subfolder.
async function resolveProjectFolder(builderFolder, projectName) {
  if (!builderFolder) return null;
  const subfolders = await listSubfolders(builderFolder);
  return findBestFolderMatch(projectName, subfolders);
}

// ─── BUILDER FOLDER → ALL IMAGES (builder-wide, cached) ────────
// Returns EVERY image URL found under a builder's Supabase Storage folder,
// searching both directly inside it and inside its project subfolders
// (bucket layout is nested Builder/Project/*.jpg). Cached per builder so
// re-renders don't re-hit Supabase. Powers the Developers page per-builder
// hero carousels, where showing the builder's full body of work is correct.
const _builderGalleryCache = new Map(); // builderName → Promise<string[]>

export function builderFolderImages(builderName) {
  const key = (builderName || "").trim();
  if (!key) return Promise.resolve([]);
  if (_builderGalleryCache.has(key)) return _builderGalleryCache.get(key);

  const promise = (async () => {
    const resolved = await resolveBuilderFolder(key);
    if (resolved) {
      const urls = await listFolderImagesDeep(resolved);
      if (urls.length) return urls;
    }
    // Fallback: legacy flat layout (builder name IS the folder, images
    // directly inside it, no bucket listing available/matched).
    return listFolderImages(key);
  })();

  _builderGalleryCache.set(key, promise);
  return promise;
}

export async function builderFirstImage(builderName) {
  const urls = await builderFolderImages(builderName);
  return urls.length ? urls[0] : null;
}

// ─── PROJECT FOLDER → IMAGES (project-specific, cached) ────────
// Returns EVERY image URL for ONE specific project: resolves the builder's
// real folder, then the project's real subfolder inside it, then lists the
// images directly inside that subfolder. This is what per-project cards
// (Properties list, Home featured, Builder page grid) should use — NOT the
// builder-wide gallery above, which would mix in every other project's
// photos under the same builder.
const _projectGalleryCache = new Map(); // "builder||project" → Promise<string[]>

export function projectImages(builderName, projectName) {
  const bKey = (builderName || "").trim();
  const pKey = (projectName || "").trim();
  if (!bKey || !pKey) return Promise.resolve([]);
  const cacheKey = `${bKey}||${pKey}`;
  if (_projectGalleryCache.has(cacheKey)) return _projectGalleryCache.get(cacheKey);

  const promise = (async () => {
    const builderFolder = await resolveBuilderFolder(bKey);
    if (builderFolder) {
      const projectFolder = await resolveProjectFolder(builderFolder, pKey);
      if (projectFolder) {
        const urls = await listFolderImages(`${builderFolder}/${projectFolder}`);
        if (urls.length) return urls;
      }
    }
    // Fallback: legacy flat layout (top-level folder named after the
    // project itself, e.g. "project-images/Bhanodaya Crystals/1.jpeg").
    return listFolderImages(pKey);
  })();

  _projectGalleryCache.set(cacheKey, promise);
  return promise;
}

export async function projectFirstImage(builderName, projectName) {
  const urls = await projectImages(builderName, projectName);
  return urls.length ? urls[0] : null;
}

export function builderSlug(name) {
  return (name || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/(^-|-$)/g, "");
}

export const faqsAPI = {
  list: (search) =>
    apiFetch(`/faqs${search ? `?search=${encodeURIComponent(search)}` : ""}`),
  create: (data) =>
    apiFetch("/faqs", { method: "POST", body: JSON.stringify(data) }),
  update: (id, data) =>
    apiFetch(`/faqs/${id}`, { method: "PUT", body: JSON.stringify(data) }),
  delete: (id) => apiFetch(`/faqs/${id}`, { method: "DELETE" }),
};

// ─── LEGACY SHIM ─────────────────────────────────────────────
export async function saveLeadToAPI(lead) {
  try {
    const res = await leadsAPI.create(lead);
    return res.lead;
  } catch (err) {
    console.error("Lead save failed:", err.message);
    const existing = JSON.parse(localStorage.getItem("gbf_leads_offline") || "[]");
    const offline = { id: `offline_${Date.now()}`, createdAt: new Date().toISOString(), ...lead };
    existing.unshift(offline);
    localStorage.setItem("gbf_leads_offline", JSON.stringify(existing));
    return offline;
  }
}